import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getCompanyById, getTIMCompanyReport } from "../../axios/axiosFunctions";

const ratingLabel = (pct) => {
  const p = Number(pct);
  if (Number.isNaN(p)) return { label: "–", color: "default" };
  if (p >= 90) return { label: "STRENGTH", color: "success" };
  if (p >= 60) return { label: "DEVELOPING", color: "info" };
  return { label: "OPPORTUNITY", color: "warning" };
};

const formatDate = (d) => {
  if (!d) return "–";
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "–";
  }
};

/**
 * Company-level rollup report (W3). Pools all completed TIM teams in a company
 * and renders aggregated dimension scores plus per-team summary.
 */
const CompanyReport = () => {
  const { companyId, slugAssessment } = useParams();
  // Per-team report links must point at the same assessment the company belongs
  // to. The slug comes from the route; fall back to TIM for the legacy path.
  const teamReportSlug = slugAssessment || "tim_self_assessment";
  const [report, setReport] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [reportRes, companyRes] = await Promise.all([
        getTIMCompanyReport(companyId),
        getCompanyById(companyId),
      ]);
      setReport(reportRes);
      setCompany(companyRes?.company || companyRes);
      setLoading(false);
    };
    load();
  }, [companyId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ padding: "80px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report || !report.data || report.data.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ padding: "48px" }}>
        <Typography variant="h3" color="secondary">
          {company?.name || "Company"} — Teams in Motion® Rollup
        </Typography>
        <Alert severity="info" sx={{ marginTop: "20px" }}>
          No completed team reports available for this company yet.
        </Alert>
      </Container>
    );
  }

  const blockEntries = report.data.filter((d) => d.blockId);
  const summaryEntry = report.data.find((d) => d.name === "Summary");

  return (
    <Container maxWidth="lg" sx={{ padding: "48px", backgroundColor: "#e8f4f8" }}>
      <Stack spacing={1} sx={{ marginBottom: "20px" }}>
        <Typography variant="overline" color="text.secondary">
          Teams in Motion® — Company Rollup
        </Typography>
        <Typography variant="h3" color="secondary" fontWeight={500}>
          {company?.name || "Company"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Aggregated across {report.completedTeamIds?.length || 0} completed team
          {(report.completedTeamIds?.length || 0) === 1 ? "" : "s"} — generated {formatDate(new Date())}
        </Typography>
      </Stack>

      {summaryEntry && (
        <Paper elevation={1} sx={{ padding: "24px", marginBottom: "32px" }}>
          <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: "12px" }}>
            Overall Score
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h2" color="secondary" fontWeight={600}>
              {summaryEntry.positivePercentage}%
            </Typography>
            <Chip {...ratingLabel(summaryEntry.positivePercentage)} sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: "8px" }}>
            Based on {summaryEntry.total} responses across all completed teams.
          </Typography>
        </Paper>
      )}

      <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: "16px" }}>
        Dimension Scores
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: "32px" }}>
        {blockEntries.map((b) => {
          const pct = Number(b.positivePercentage) || 0;
          const rating = ratingLabel(b.positivePercentage);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={b.blockId}>
              <Paper sx={{ padding: "16px", borderTop: `4px solid ${b.color || "#1976d2"}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, minHeight: "48px" }}>
                  {b.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginTop: "8px" }}>
                  <Typography variant="h4" fontWeight={600}>{pct.toFixed(1)}%</Typography>
                  <Chip size="small" {...rating} />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(pct, 100)}
                  sx={{ marginTop: "12px", height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ marginTop: "6px", display: "block" }}>
                  {b.totalPositive} positive of {b.total} responses
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ marginY: "24px" }} />

      <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: "16px" }}>
        Teams Included
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Members</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Launched</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Deadline</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Report</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.teams?.map((t) => (
              <TableRow key={t._id} hover>
                <TableCell>{t.name}</TableCell>
                <TableCell>
                  {t.completed ? (
                    <Chip size="small" label="Completed" color="success" />
                  ) : (
                    <Chip size="small" label="In progress" color="default" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>{t.memberCount}</TableCell>
                <TableCell>{formatDate(t.launchDate)}</TableCell>
                <TableCell>{formatDate(t.deadline)}</TableCell>
                <TableCell align="right">
                  {t.completed ? (
                    <a href={`/${teamReportSlug}/${t._id}/report`} target="_blank" rel="noopener noreferrer">
                      Open
                    </a>
                  ) : (
                    "–"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default CompanyReport;
